import React, { useState } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';
import {
  clearCurrentYearAccommodationHistory,
  getCampYear
} from '../../../utils/accommodationHistoryUtils';
import {
  getRegistrationPlaceCode,
  isCancelledRegistrationStatus,
  normalizePlaceCode,
} from '../../../utils/registrationPlaceCode';

interface UndoReservationCardProps {
  registration: Registration;
  onRegistrationUpdate: (updatedRegistration: Registration) => void;
  onResetHoldSteps?: () => void;
  onResetReleaseStep?: () => void;
}

const UndoReservationCard: React.FC<UndoReservationCardProps> = ({
  registration,
  onRegistrationUpdate,
  onResetHoldSteps,
  onResetReleaseStep
}) => {
  const [processing, setProcessing] = useState(false);

  const handleUndoReservation = async () => {
    if (processing) return;
    
    if (!window.confirm('Are you sure you want to undo this reservation? This will set the status to Pending and mark the accommodation as available.')) {
      return;
    }
    
    setProcessing(true);

    try {
      const placeCode = getRegistrationPlaceCode(registration);

      // Step 1: Check if other registrations share this accommodation
      let othersInAccommodation = false;
      if (placeCode) {
        const querySnapshot = await getDocs(collection(db, 'Registrations'));

        let otherCount = 0;
        querySnapshot.forEach((docSnap) => {
          if (docSnap.id === registration.id) return;

          const data = docSnap.data();
          if (isCancelledRegistrationStatus(data.status)) return;
          if (getRegistrationPlaceCode(data) !== placeCode) return;

          otherCount++;
        });
        othersInAccommodation = otherCount > 0;
      }

      // Step 2: Mark accommodation as available (only if no others are assigned)
      if (placeCode && !othersInAccommodation) {
        const inventoryRef = doc(db, 'inventory', 'accommodations');
        const inventorySnap = await getDoc(inventoryRef);

        if (inventorySnap.exists()) {
          const inventoryData = inventorySnap.data();
          const accommodationTypes = ['Apartment', 'Cottage', 'Fireside', 'LowerCourt', 'UpperCourt', 'dormA', 'dormB', 'privateCottage'];

          for (const type of accommodationTypes) {
            if (inventoryData[type] && Array.isArray(inventoryData[type])) {
              const accommodations = inventoryData[type];
              const index = accommodations.findIndex((item: any) =>
                normalizePlaceCode(item.placeCode) === placeCode
              );

              if (index !== -1) {
                accommodations[index].status = 'available';
                await updateDoc(inventoryRef, { [type]: accommodations });
                break;
              }
            }
          }
        }
      }

      const primaryCamperId = registration?.registrationInfo?.primaryCamperId;
      if (primaryCamperId) {
        const campYear = await getCampYear();
        await clearCurrentYearAccommodationHistory(primaryCamperId, campYear);
      }

      // Step 3: Set registration status to Pending and clear invoice number
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        status: 'Pending',
        invoiceNumber: ''
      });

      // Step 4: Reset checkmarks in Hold and Release cards
      if (onResetHoldSteps) onResetHoldSteps();
      if (onResetReleaseStep) onResetReleaseStep();

      // Update local state
      const updatedRegistration = {
        ...registration,
        status: 'Pending',
        invoiceNumber: ''
      };
      onRegistrationUpdate(updatedRegistration);
    } catch (error) {
      console.error('Error undoing reservation:', error);
      alert('Failed to undo reservation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const canUndo = getRegistrationPlaceCode(registration) &&
                  (registration?.status === 'Hold' || registration?.status === 'Assigned');

  return (
    <div className="card mb-3" style={{ borderColor: '#007bff', borderWidth: '2px' }}>
      <div className="card-body p-3">
        <button
          onClick={handleUndoReservation}
          disabled={!canUndo || processing}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: canUndo && !processing ? '#dc3545' : '#dee2e6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: canUndo && !processing ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          {processing ? 'Processing...' : 'Undo Reservation'}
        </button>

        {!canUndo && (
          <p className="text-muted mt-2" style={{ fontSize: '0.85rem', marginBottom: 0, textAlign: 'center' }}>
            Only available when reservation is on Hold or Assigned
          </p>
        )}
      </div>
    </div>
  );
};

export default UndoReservationCard;

